import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ClienteService, GeradorService } from "@/services/StorageService";
import { Cliente, Gerador, FinanceiroResumo } from "@/types";
import { useAuth } from "@/contexts/AuthContext";

export const useFinanceiroGerador = (geradorId: string | undefined) => {
  const { canAccessResource } = useAuth();
  const [gerador, setGerador] = useState<Gerador | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [search, setSearch] = useState("");
  const [editingTarifas, setEditingTarifas] = useState<Record<string, number>>({});
  const [originalTarifas, setOriginalTarifas] = useState<Record<string, number>>({});
  const [editingTarifasGerador, setEditingTarifasGerador] = useState<Record<string, number>>({});
  const [originalTarifasGerador, setOriginalTarifasGerador] = useState<Record<string, number>>({});
  const [resumo, setResumo] = useState<FinanceiroResumo>({
    totalClientes: 0,
    totalConsumo: 0,
    percentualUsoGerador: 0,
    totalEconomia: 0,
    gastoMedioClientes: 0,
    receitaTotal: 0
  });

  useEffect(() => {
    if (!geradorId) return;
    
    const geradorData = GeradorService.getById(geradorId);
    if (!geradorData) {
      toast.error("Gerador não encontrado");
      return;
    }
    
    if (!canAccessResource(geradorData.criadoPor)) {
      toast.error("Você não tem permissão para acessar este recurso");
      return;
    }
    
    setGerador(geradorData);
    
    const clientesDoGerador = ClienteService.getByGeradorId(geradorId);
    
    const clientesComValorCalculado = clientesDoGerador.map(cliente => {
      const tarifaGerador = cliente.tarifaGerador !== undefined 
        ? cliente.tarifaGerador 
        : geradorData.tarifaVenda;
        
      return {
        ...cliente,
        valorCalculado: cliente.gastoEnergia * tarifaGerador
      };
    });
    
    setClientes(clientesComValorCalculado);
    setFilteredClientes(clientesComValorCalculado);
    
    const tarifasMap: Record<string, number> = {};
    const originalMap: Record<string, number> = {};
    const tarifasGeradorMap: Record<string, number> = {};
    const originalGeradorMap: Record<string, number> = {};
    
    clientesComValorCalculado.forEach(cliente => {
      tarifasMap[cliente.id] = cliente.tarifaEnergia;
      originalMap[cliente.id] = cliente.tarifaEnergia;
      
      const tarifaGerador = cliente.tarifaGerador !== undefined 
        ? cliente.tarifaGerador 
        : geradorData.tarifaVenda;
        
      tarifasGeradorMap[cliente.id] = tarifaGerador;
      originalGeradorMap[cliente.id] = tarifaGerador;
    });
    
    setEditingTarifas(tarifasMap);
    setOriginalTarifas(originalMap);
    setEditingTarifasGerador(tarifasGeradorMap);
    setOriginalTarifasGerador(originalGeradorMap);
    
    calcularResumo(clientesComValorCalculado, geradorData);
    
    const receitaTotal = clientesComValorCalculado.reduce((sum, cliente) => 
      sum + (cliente.valorCalculado || 0), 0);
    
    GeradorService.update(geradorId, {
      ...geradorData,
      receitaTotal: receitaTotal
    });
    
  }, [geradorId, canAccessResource]);

  useEffect(() => {
    if (search && clientes.length > 0) {
      const filtered = clientes.filter(cliente => 
        cliente.nome.toLowerCase().includes(search.toLowerCase()) ||
        cliente.numeroCliente.includes(search) ||
        cliente.numeroInstalacao.includes(search) ||
        cliente.cidade.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredClientes(filtered);
    } else {
      setFilteredClientes(clientes);
    }
  }, [search, clientes]);

  const calcularResumo = (clientesList: Cliente[], geradorData: Gerador) => {
    if (clientesList.length === 0) {
      setResumo({
        totalClientes: 0,
        totalConsumo: 0,
        percentualUsoGerador: 0,
        totalEconomia: 0,
        gastoMedioClientes: 0,
        receitaTotal: 0
      });
      return;
    }
    
    const totalClientes = clientesList.length;
    const totalConsumo = clientesList.reduce((sum, cliente) => sum + (cliente.consumoMedio || 0), 0);
    const percentualUsoGerador = geradorData.geracaoEstimada > 0 
      ? (totalConsumo / geradorData.geracaoEstimada) * 100 
      : 0;
      
    const totalEconomia = clientesList.reduce((sum, cliente) => {
      const tarifaGerador = cliente.tarifaGerador !== undefined 
        ? cliente.tarifaGerador 
        : geradorData.tarifaVenda;
        
      const consumoMedio = cliente.consumoMedio || 0;
      const economia = consumoMedio * (cliente.tarifaEnergia - tarifaGerador);
      return sum + economia;
    }, 0);
    
    const totalGasto = clientesList.reduce((sum, cliente) => sum + cliente.gastoEnergia, 0);
    const gastoMedioClientes = totalClientes > 0 ? totalGasto / totalClientes : 0;
    
    const receitaTotal = clientesList.reduce((sum, cliente) => {
      const tarifaGerador = cliente.tarifaGerador !== undefined 
        ? cliente.tarifaGerador 
        : geradorData.tarifaVenda;
        
      return sum + (cliente.valorCalculado || cliente.gastoEnergia * tarifaGerador);
    }, 0);
    
    setResumo({
      totalClientes,
      totalConsumo,
      percentualUsoGerador,
      totalEconomia,
      gastoMedioClientes,
      receitaTotal
    });
  };

  const handleTarifaChange = (clienteId: string, value: string) => {
    const tarifa = parseFloat(value);
    if (!isNaN(tarifa) && tarifa > 0) {
      setEditingTarifas({
        ...editingTarifas,
        [clienteId]: tarifa
      });
    }
  };

  const handleSaveTarifa = (clienteId: string) => {
    if (!gerador) return;
    
    const cliente = clientes.find(c => c.id === clienteId);
    if (!cliente) return;
    
    const novaTarifa = editingTarifas[clienteId];
    
    if (novaTarifa === cliente.tarifaEnergia) {
      toast.info("Nenhuma alteração na tarifa");
      return;
    }
    
    const clienteAtualizado = ClienteService.update(clienteId, {
      tarifaEnergia: novaTarifa
    });
    
    if (clienteAtualizado) {
      const tarifaGerador = clienteAtualizado.tarifaGerador !== undefined 
        ? clienteAtualizado.tarifaGerador 
        : gerador.tarifaVenda;
        
      const valorCalculado = clienteAtualizado.gastoEnergia * tarifaGerador;
      
      const clienteComValor = {
        ...clienteAtualizado,
        valorCalculado: valorCalculado
      };
      
      const novosClientes = clientes.map(c => 
        c.id === clienteId ? clienteComValor : c
      );
      
      setClientes(novosClientes);
      setFilteredClientes(search ? 
        novosClientes.filter(c => c.nome.toLowerCase().includes(search.toLowerCase())) : 
        novosClientes
      );
      
      setOriginalTarifas({
        ...originalTarifas,
        [clienteId]: novaTarifa
      });
      
      if (gerador) {
        calcularResumo(novosClientes, gerador);
        
        const receitaTotal = novosClientes.reduce((sum, cliente) => {
          const taxaGerador = cliente.tarifaGerador !== undefined 
            ? cliente.tarifaGerador 
            : gerador.tarifaVenda;
          return sum + (cliente.valorCalculado || cliente.gastoEnergia * taxaGerador);
        }, 0);
        
        GeradorService.update(geradorId, {
          ...gerador,
          receitaTotal: receitaTotal
        });
      }
      
      toast.success("Tarifa do cliente atualizada com sucesso");
    } else {
      toast.error("Erro ao atualizar tarifa");
    }
  };

  const handleTarifaGeradorChange = (clienteId: string, value: string) => {
    const tarifa = parseFloat(value);
    if (!isNaN(tarifa) && tarifa > 0) {
      setEditingTarifasGerador({
        ...editingTarifasGerador,
        [clienteId]: tarifa
      });
    }
  };

  const handleSaveTarifaGerador = (clienteId: string) => {
    if (!gerador) return;
    
    const cliente = clientes.find(c => c.id === clienteId);
    if (!cliente) return;
    
    const novaTarifaGerador = editingTarifasGerador[clienteId];
    const tarifaGeradorAtual = cliente.tarifaGerador !== undefined 
      ? cliente.tarifaGerador 
      : gerador.tarifaVenda;
    
    if (novaTarifaGerador === tarifaGeradorAtual) {
      toast.info("Nenhuma alteração na tarifa do gerador");
      return;
    }
    
    const clienteAtualizado = ClienteService.update(clienteId, {
      ...cliente,
      tarifaGerador: novaTarifaGerador
    });
    
    if (clienteAtualizado) {
      const valorCalculado = clienteAtualizado.gastoEnergia * novaTarifaGerador;
      
      const clienteComValor = {
        ...clienteAtualizado,
        valorCalculado: valorCalculado
      };
      
      const consumoMedio = clienteAtualizado.gastoEnergia / clienteAtualizado.tarifaEnergia;
      const economiaEstimada = consumoMedio * (clienteAtualizado.tarifaEnergia - novaTarifaGerador);
      const percentualEconomia = clienteAtualizado.gastoEnergia > 0 
        ? (economiaEstimada / clienteAtualizado.gastoEnergia) * 100 
        : 0;
        
      clienteComValor.economiaEstimada = economiaEstimada;
      clienteComValor.percentualEconomia = percentualEconomia;
      
      const novosClientes = clientes.map(c => 
        c.id === clienteId ? clienteComValor : c
      );
      
      setClientes(novosClientes);
      setFilteredClientes(search ? 
        novosClientes.filter(c => c.nome.toLowerCase().includes(search.toLowerCase())) : 
        novosClientes
      );
      
      setOriginalTarifasGerador({
        ...originalTarifasGerador,
        [clienteId]: novaTarifaGerador
      });
      
      if (gerador) {
        calcularResumo(novosClientes, gerador);
        
        const receitaTotal = novosClientes.reduce((sum, cliente) => {
          const taxaGerador = cliente.tarifaGerador !== undefined 
            ? cliente.tarifaGerador 
            : gerador.tarifaVenda;
          return sum + (cliente.valorCalculado || cliente.gastoEnergia * taxaGerador);
        }, 0);
        
        GeradorService.update(geradorId, {
          ...gerador,
          receitaTotal: receitaTotal
        });
      }
      
      toast.success("Tarifa do gerador para este cliente atualizada com sucesso");
    } else {
      toast.error("Erro ao atualizar tarifa do gerador");
    }
  };

  const handleUndoTarifa = (clienteId: string) => {
    setEditingTarifas({
      ...editingTarifas,
      [clienteId]: originalTarifas[clienteId]
    });
  };

  const handleUndoTarifaGerador = (clienteId: string) => {
    setEditingTarifasGerador({
      ...editingTarifasGerador,
      [clienteId]: originalTarifasGerador[clienteId]
    });
  };

  const handleExport = () => {
    if (filteredClientes.length === 0 || !gerador) return;
    
    const headers = [
      'Nome', 'Documento', 'Nº Cliente', 'Nº Instalação', 
      'Endereço', 'Cidade/UF', 'Gasto (R$)', 'Tarifa (R$/kWh)', 
      'Consumo (kWh)', 'Economia (R$)', 'Economia (%)', 
      'Uso do Gerador (%)', 'Valor Calculado (R$)'
    ];
    
    const rows = filteredClientes.map(c => [
      c.nome,
      c.documento,
      c.numeroCliente,
      c.numeroInstalacao,
      `${c.logradouro}, ${c.bairro}`,
      `${c.cidade}/${c.estado}`,
      c.gastoEnergia.toString(),
      c.tarifaEnergia.toString(),
      (c.consumoMedio || 0).toString(),
      (c.economiaEstimada || 0).toString(),
      (c.percentualEconomia || 0).toString(),
      (c.percentualEnergiaAdquirida || 0).toString(),
      (c.valorCalculado || (c.gastoEnergia * gerador.tarifaVenda)).toString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `financeiro_${gerador.nomeTitular.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    gerador,
    clientes: filteredClientes,
    search,
    setSearch,
    resumo,
    editingTarifas,
    originalTarifas,
    editingTarifasGerador,
    originalTarifasGerador,
    handleTarifaChange,
    handleSaveTarifa,
    handleUndoTarifa,
    handleTarifaGeradorChange,
    handleSaveTarifaGerador,
    handleUndoTarifaGerador,
    handleExport
  };
};
